import React from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../../helper/formatDate";
import ParentHeader from "../../Parent/ParentHeader/ParentHeader";
import { Msr, useMaterialSymbols } from "./components";
import { getOireachtasEvent, getRoster, getCart, getTeams } from "./api";
import { regWindow, lateFeeTag, closeTimeLabel } from "./deadlines";
import "./oireachtas.css";
import "./t1.css";

// T1 — the Oireachtas event dashboard. Publicly viewable; the register/status
// panel is teacher-only (registrationTeacherOnly — parents see the read-only
// "your teacher registers" note). Reconciled to the spec mockup
// teacher-registration-screens/1-event-dashboard.html.

// formatDate strips the UTC offset so a stored midnight-UTC date doesn't render
// a day early west of UTC (the repo-wide pattern).
const asMoment = (d) => (d ? moment(formatDate(d)) : null);
const fmt = (d) => (d ? asMoment(d).format("MMMM D, YYYY") : null);

// "Nov 27–29, 2026" / "Nov 27 – Dec 1, 2026" from the eventDays array.
function fmtDays(days) {
  if (!days || !days.length) return null;
  const start = asMoment(days[0]);
  const end = asMoment(days[days.length - 1]);
  if (!start.isValid()) return null;
  if (!end.isValid() || start.isSame(end, "day")) return start.format("MMM D, YYYY");
  if (start.isSame(end, "month")) return `${start.format("MMM D")}–${end.format("D, YYYY")}`;
  return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
}

// Deadline banner off the shared registration-window mirror (deadlines.js —
// lockstep with the server gate: no late window => close at entriesCloseDate;
// each close date ends 11:59 pm in the event's time zone).
function deadlineBanner(event) {
  const w = regWindow(event);
  if (w.state === "closed") {
    return { cls: "closed", icon: "lock", main: "Registration is closed", sub: "The entry window for this Oireachtas has ended." };
  }
  if (w.state === "late") {
    const lf = event.fees && event.fees.lateFee;
    const tag = lateFeeTag(event);
    const sub = tag && lf.mode === "percent"
      ? `A ${lf.value}% surcharge applies to all entries in the late window.`
      : "Additional late fees apply to entries in this window.";
    return { cls: "late", icon: "warning", main: `Late entries until ${fmt(event.lateEntryCloseDate)} at ${closeTimeLabel(event, event.lateEntryCloseDate)}`, sub, tag };
  }
  if (w.entriesCloseDate) {
    // Days-left counts to the NORMAL close here (banner copy), not the hard
    // close. The close instant is the END of the close day, so the close day
    // itself counts as 0 ("today is the last day").
    const days = Math.max(0, Math.ceil((w.entriesCloseDate - new Date()) / 86400000) - 1);
    const sub = days > 0
      ? `${days} day${days === 1 ? "" : "s"} left to submit your school's entries.`
      : "Today is the last day to submit your school's entries.";
    return { cls: "default", icon: "schedule", main: `Entries close ${fmt(event.entriesCloseDate)} at ${closeTimeLabel(event, event.entriesCloseDate)}`, sub };
  }
  return null;
}

export default function OireachtasEventPage() {
  useMaterialSymbols();
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reg, setReg] = React.useState(null); // teacher-scoped: { school, dancers, teams, total, status }
  const isTeacher = localStorage.getItem("isTeacher") === "true"; // set at SignIn

  React.useEffect(() => {
    getOireachtasEvent(id)
      .then((ev) => {
        setEvent(ev);
        document.title = `Feis.link - ${ev.name || "Oireachtas"}`;
      })
      .catch((e) => setError(e.messages || "Event not found."));
  }, [id]);

  // Teacher-only reads (roster/cart/quote are teacher-gated server-side). Failures
  // degrade to an empty "Not started" panel rather than breaking the page.
  React.useEffect(() => {
    if (!isTeacher) return;
    let alive = true;
    (async () => {
      // Status only — counts and money live on Current entries (one number,
      // one home; the old stat row re-quoted the draft on every page view).
      // Hidden roster dancers and team entries count as entries too: a school
      // whose paid entries are only those must still see them.
      const [rosterR, cartR, teamsR] = await Promise.allSettled([
        getRoster(id, true), getCart(id), getTeams(id),
      ]);
      if (!alive) return;
      const roster = rosterR.status === "fulfilled" ? rosterR.value || [] : [];
      const cart = cartR.status === "fulfilled" ? cartR.value : null;
      const teams = teamsR.status === "fulfilled" ? teamsR.value || [] : [];

      const cartItems = (cart && cart.items) || [];
      const cartTeams = (cart && cart.teams) || [];
      const paidCount = roster.filter((d) => d.entry).length + teams.length;
      const hasDraft = cartItems.length > 0 || cartTeams.length > 0;
      const status = hasDraft ? "draft" : paidCount > 0 ? "paid" : "none";

      setReg({
        school: roster.length && roster[0].schoolName,
        status,
        hasDraft,
        // The entries lookup itself failed: don't claim "Not started".
        unknown: rosterR.status !== "fulfilled",
      });
    })();
    return () => { alive = false; };
  }, [id, isTeacher]);

  if (error) return <div className="oir"><ParentHeader /><div className="loading">{error}</div></div>;
  if (!event) return <div className="oir"><ParentHeader /><div className="loading">Loading…</div></div>;

  const banner = deadlineBanner(event);
  const closed = banner && banner.cls === "closed";
  const venue = [event.venue_name, event.venue_city].filter(Boolean).join(", ");
  const days = fmtDays(event.eventDays);
  const schoolLabel = (reg && reg.school) || "your school";

  const status = reg ? reg.status : "none";
  const paid = status === "paid";
  const cta = status === "none"
    ? { label: "Register dancers", icon: "group_add" }
    : status === "draft"
      ? { label: "Continue draft", icon: "arrow_forward" }
      : { label: "Add entries", icon: "add" };

  return (
    <div className="oir">
      <ParentHeader />
      <div className="wrap narrow">
        {/* event header */}
        <div className="ev-head">
          <div className="ev-logo">
            {event.logo ? <img src={event.logo} alt="" /> : <Msr style={{ fontSize: 34, color: "var(--fl-green)" }}>emoji_events</Msr>}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="ev-cat">{event.eventType || "Oireachtas"} · Championship</div>
            <h1 className="ev-title">{event.name}</h1>
            <div className="ev-meta">
              {venue && <span><Msr>location_on</Msr>{venue}</span>}
              {days && <span><Msr>calendar_month</Msr>{days}</span>}
            </div>
          </div>
        </div>

        {/* who you're registering for (teacher only) */}
        {isTeacher && (
          <div className="ctx"><Msr>school</Msr>Registering on behalf of {schoolLabel}.</div>
        )}

        {/* deadline banner */}
        {banner && (
          <div className={"banner " + banner.cls}>
            <span className="b-ic"><Msr>{banner.icon}</Msr></span>
            <div>
              <div className="b-main">{banner.main}</div>
              <div className="b-sub">{banner.sub}</div>
            </div>
            {banner.tag && <span className="b-tag">{banner.tag}</span>}
          </div>
        )}

        {/* teacher status panel / parent read-only note */}
        {isTeacher ? (
          <div className="panel">
            <div className="panel-head">
              <h2>{schoolLabel}</h2>
              {status === "paid" ? (
                <span className="pill paid"><Msr>check_circle</Msr>Entries submitted · Paid</span>
              ) : status === "draft" ? (
                <span className="pill draft"><Msr>edit_note</Msr>Draft in progress</span>
              ) : reg && reg.unknown ? null : (
                <span className="pill none"><Msr>radio_button_unchecked</Msr>Not started</span>
              )}
            </div>

            {status === "none" ? (
              <div className="empty">
                <div className="em-ic"><Msr>group_add</Msr></div>
                <h3>No entries yet</h3>
                <p>Register your dancers for {event.name}. You can save a draft and come back anytime before the deadline.</p>
                <button type="button" className="bigbtn" disabled={closed}
                  onClick={() => navigate(`/teacher/oireachtas/${id}/register`)}>
                  <Msr>{cta.icon}</Msr>{cta.label}
                </button>
                {closed && <div className="note" style={{ justifyContent: "center" }}><Msr>info</Msr>Registration has closed for this event.</div>}
                {/* After the close, or when the entries couldn't be loaded,
                    a teacher can always open Current entries. */}
                {(closed || (reg && reg.unknown)) && (
                  <button type="button" className="seclink" onClick={() => navigate(`/teacher/oireachtas/${id}/entries`)}>
                    <Msr>visibility</Msr>View current entries
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="panel-foot">
                  <button type="button" className="bigbtn" disabled={closed}
                    onClick={() => navigate(`/teacher/oireachtas/${id}/register`)}>
                    <Msr>{cta.icon}</Msr>{cta.label}
                  </button>
                  <span className="foot-spacer" />
                  <button type="button" className="seclink" onClick={() => navigate(`/teacher/oireachtas/${id}/entries`)}>
                    <Msr>visibility</Msr>View current entries
                  </button>
                </div>
                {closed && <div className="note" style={{ padding: "0 26px 22px" }}><Msr>info</Msr>Registration is closed — entries can no longer be changed.</div>}
                {paid && !closed && <div className="note" style={{ padding: "0 26px 22px" }}><Msr>check_circle</Msr>Payment received. You can still add entries until the deadline.</div>}
              </>
            )}
          </div>
        ) : (
          <div className="panel">
            <div className="panel-head"><h2>Registration</h2></div>
            <div className="empty">
              <div className="em-ic"><Msr>school</Msr></div>
              <h3>Registration is handled by your dance teacher</h3>
              <p>Your teacher registers the whole school for this Oireachtas. Make sure your dancer's profile lists the correct school so they appear on the school's roster.</p>
            </div>
          </div>
        )}

        {event.description && (
          <div className="evcard" style={{ marginTop: 16 }}>
            <h2>About</h2>
            <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{event.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}
